import { useEffect, useRef } from 'react'
import { NetworkStatus } from '@apollo/client'
import { heapEvent } from '@equipmentshare/heaper'
import { StoredFilterParams } from '@equipmentshare/dcf-search-filter-experience'
import { useProductsQuery, ProductsQuery } from '../../../../graphql'
import { readSfePaging, readSfeParam, useCursorPaging } from '../../../../hooks/sfePaging'

export type ProductRow = NonNullable<ProductsQuery['products']['edges'][number]['node']>

export type ProductsSearchRequest = {
	search?: string,
	page?: number,
	size?: number,
}

export default function useProductsDataQuery( params: StoredFilterParams<ProductsSearchRequest> ) {
	const { page, size } = readSfePaging( params )
	const search = readSfeParam( params, 'search' )

	// A search already in the URL on load (reload, back, shared link) wasn't typed, so it isn't tracked.
	const trackedSearchRef = useRef( search )
	useEffect( () => {
		if ( search && search !== trackedSearchRef.current ) {
			heapEvent( 'Inventory - Parts Catalog - Change - Search Input', { search_term: search } )
		}
		trackedSearchRef.current = search
	}, [ search ] )

	const { after, recordEndCursor } = useCursorPaging( page, JSON.stringify( { search, size } ) )

	const { data, previousData, networkStatus } = useProductsQuery( {
		variables: {
			search,
			paging: { first: size, after },
		},
		notifyOnNetworkStatusChange: true,
	} )

	recordEndCursor( data?.products.pageInfo.endCursor )

	const displayData = data ?? previousData
	const rows = displayData?.products.edges.map( ( edge ) => edge.node ) ?? []

	return {
		isFetching: networkStatus !== NetworkStatus.ready,
		data: displayData && {
			rows,
			// Unlike the other V* queries, `products` has a real total.
			rowCount: displayData.products.totalCount,
		},
	}
}
